import { RightOutlined } from '@ant-design/icons';
import CaregiverAvatarList from 'components/lists/CaregiverAvatarList';
import Logo from 'components/navigation/Logo';
import { Button } from 'components/ui/button';
import { Carousel, CarouselApi, CarouselContent, CarouselDots, CarouselItem } from 'components/ui/carousel';
import OnboardingV2Page from 'pages/OnboardingV2';
import posthog from 'posthog-js';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { routes } from 'services/RouteService';

export default function OnboardingPage() {
  const variant = posthog.getFeatureFlag('onboarding-slides-v3');
  if (variant === 'new-slides') {
    return <OnboardingV2Page />;
  }
  if (variant === 'no-slides') {
    return <NoSlidesOnboardingPage />;
  }
  return <OnboardingLegacyPage />;
}

// Skips the carousel entirely and sends the user straight to whatever the
// end of the carousel would have led to.
function NoSlidesOnboardingPage() {
  const navigate = useNavigate();

  useEffect(() => {
    navigate(routes.home.route, { replace: true });
  }, [navigate]);

  return null;
}

const emphasisClassName = 'font-medium text-foreground';
const sectionClassName = 'flex justify-center p-2.5 text-center';

function OnboardingLegacyPage() {
  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [carousel, setCarousel] = useState<CarouselApi>();

  const goToPage = (route: string) => {
    navigate(route);
  };

  const nextPage = (isLastPage: boolean) => {
    if (isLastPage) {
      goToPage(routes.home.route);
    } else {
      // Display the next page
      carousel?.scrollNext();
    }
  };

  const carouselPages = [
    // Page 1
    {
      content: (
        <div>
          <p>
            <span>
              {t('onboarding.carousel.pages.one.text1start')}
              <span className={emphasisClassName}>{t('onboarding.carousel.pages.one.text1Bold1')}</span>
              {t('onboarding.carousel.pages.one.text1middle')}
              <span className={emphasisClassName}>{t('onboarding.carousel.pages.one.text1Bold2')}</span>
              {t('onboarding.carousel.pages.one.text1end')}
            </span>
          </p>
          <p>
            <span>
              {t('onboarding.carousel.pages.one.text2start')}
              <span className={emphasisClassName}>{t('onboarding.carousel.pages.one.text2Bold1')}</span>
              {t('onboarding.carousel.pages.one.text2middle')}
              <span className={emphasisClassName}>{t('onboarding.carousel.pages.one.text2Bold2')}</span>
            </span>
          </p>
        </div>
      ),
      scrollableContent: (
        <div className="flex flex-1 flex-col">
          <CaregiverAvatarList />
        </div>
      ),
    },

    // Page 2
    {
      scrollableContent: (
        <div>
          <p>
            <span>
              {t('onboarding.carousel.pages.tracking.textStart')}
              <span className={emphasisClassName}>{t('onboarding.carousel.pages.tracking.textBold1')}</span>
              {t('onboarding.carousel.pages.tracking.textMiddle')}
              <span className={emphasisClassName}>{t('onboarding.carousel.pages.tracking.textBold2')}</span>
              {t('onboarding.carousel.pages.tracking.textEnd')}
            </span>
          </p>
          <div className="flex justify-center">
            <img alt="Caregivers list" src={require('../assets/onboarding-tracking.png')} className="mt-4 w-3/4" />
          </div>
        </div>
      ),
    },

    // Page 3
    {
      scrollableContent: (
        <div>
          <p>
            <span>
              {t('onboarding.carousel.pages.two.textStart')}
              <span className={emphasisClassName}>{t('onboarding.carousel.pages.two.textBold1')}</span>
              {t('onboarding.carousel.pages.two.textMiddle1')}
              <span className={emphasisClassName}>{t('onboarding.carousel.pages.two.textBold2')}</span>
              {t('onboarding.carousel.pages.two.textMiddle2')}
              <span className={emphasisClassName}>{t('onboarding.carousel.pages.two.textBold3')}</span>
              {t('onboarding.carousel.pages.two.textMiddle3')}
              <span className={emphasisClassName}>{t('onboarding.carousel.pages.two.textBold4')}</span>
            </span>
          </p>
          <div className="flex justify-center">
            <img alt="Blog post samples" src={require('../assets/onboarding-blog-posts.png')} className="mt-4 w-3/4" />
          </div>
        </div>
      ),
    },

    // Page 4
    {
      scrollableContent: (
        <div>
          <p>
            <span>
              {t('onboarding.carousel.pages.three.textStart')}
              <span className={emphasisClassName}>{t('onboarding.carousel.pages.three.textBold1')}</span>
              {t('onboarding.carousel.pages.three.textMiddle1')}
              <span className={emphasisClassName}>{t('onboarding.carousel.pages.three.textBold2')}</span>
              {t('onboarding.carousel.pages.three.textEnd')}
            </span>
          </p>
          <div className="flex justify-center">
            <img
              alt="Caregivers list"
              src={require('../assets/onboarding-caregivers-list.png')}
              className="mt-4 w-3/4"
            />
          </div>
        </div>
      ),
    },
  ];

  const renderOnboardingCarouselPageTemplate = (
    index: number,
    children: { content?: React.ReactElement; scrollableContent?: React.ReactElement },
  ) => {
    const isLastPage = index === carouselPages.length - 1;
    const buttonText = isLastPage ? t('onboarding.carousel.buttons.end') : t('onboarding.carousel.buttons.next');

    return (
      <CarouselItem key={index} className="flex h-app-screen pl-0">
        <div className="flex flex-1 flex-col">
          <div className={sectionClassName}>
            <div className="pt-16">
              <Logo size="large" />
            </div>
          </div>
          <div className={sectionClassName}>
            <div>
              <h3>
                {t('onboarding.carousel.title')}
                <span className="text-foreground">{t('appName')}</span>
                {t('onboarding.carousel.titleEnd')}
              </h3>
              <p>
                <span>{t('appTagline')}</span>
              </p>
            </div>
          </div>
          {children.content && (
            <div className={`${sectionClassName} flex-none`}>
              <div>{children.content}</div>
            </div>
          )}
          {children.scrollableContent && (
            <div className={`${sectionClassName} flex-1 overflow-y-auto`}>
              <div className="flex w-full flex-col">{children.scrollableContent}</div>
            </div>
          )}
          <div className="flex justify-center p-12 text-center">
            <div>
              <Button size="lg" onClick={() => nextPage(isLastPage)}>
                <RightOutlined />
                {buttonText}
              </Button>
            </div>
          </div>
        </div>
      </CarouselItem>
    );
  };

  return (
    <Carousel
      setApi={setCarousel}
      opts={{ loop: false }}
      className="bg-background bg-[linear-gradient(0deg,var(--background)_0%,var(--card)_100%)] bg-[size:auto_100px] bg-top bg-no-repeat"
    >
      <CarouselContent className="ml-0">
        {carouselPages.map((page, index) => renderOnboardingCarouselPageTemplate(index, page))}
      </CarouselContent>
      <CarouselDots className="absolute inset-x-0 bottom-5 [&>button]:bg-subtle-foreground [&>button[data-active=true]]:bg-inverse" />
    </Carousel>
  );
}
